import './avg-video.css';
import { state } from '../shared/state';
import { $, showToast, renderMarkdownHtml } from '../shared/index';
import { switchView } from '../shared/navigation';
import {
  collectionCompositionLabel,
  escapeAttr,
  escapeHtml,
  markdownToPlain,
  needsServerPlayback,
  normalizeCollection,
  normalizeCollectionDetail,
  normalizeVideoContext,
  plural,
  posterSrc,
  episodeNumber,
  formatDuration,
  formatVideoDate,
  formatViewCount,
  resolveVideoEmbedSrc,
  rubricAccentClass,
  seasonLabel,
  type AvgVideoCollection,
  type AvgVideoCollectionDetail,
  type AvgVideoContext,
  type AvgVideoRubric,
  type AvgVideoSeason,
  type AvgVideoVideo,
} from './avg-video-shared';
import {
  buildSeriesGraph,
  buildTopicsGraph,
  renderMapSvg,
  type AvgVideoMapMode,
  type MapGraph,
} from './avg-video-map';

const PAGE_SIZE = 12;
const PROGRESS_THROTTLE_MS = 10000;
const PLAYBACK_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const HOME_ENTITY_LIMIT = 6;
/** Поиск на экране «Все видео» ходит на сервер — ждём паузы в наборе. */
const SEARCH_DEBOUNCE_MS = 300;
/**
 * Разогрев индекса карточек для стартового экрана.
 *
 * «Продолжить просмотр» приходит списком идентификаторов, а мозаика подборки
 * собирается из постеров её состава: обоим нужен индекс карточек по id, а
 * эндпоинта «видео по списку id» в контракте нет. Экран «Все видео» этот массив
 * не читает вовсе — он листается сервером.
 */
const HOME_INDEX_LIMIT = 100;

interface AvgVideoProgress {
  video_id: string;
  position_seconds: number;
  duration_seconds: number | null;
  updated_at: number;
}

interface PlaybackCacheEntry {
  video_url: string | null;
  preview_url: string | null;
  fetchedAt: number;
}

/**
 * Экраны каталога.
 *
 * `home` — карта материалов (продолжить, рекомендованное, входы в подборки и
 * сериалы). Длинный общий список больше не стартовое представление: он живёт на
 * отдельном экране `all` (AV-ADR-2, ограничения масштаба).
 */
type AtvScreen = 'home' | 'collections' | 'series' | 'all' | 'entity';

/** Разогретый индекс стартового экрана: «продолжить», мозаики, рекомендованное. */
let homeItems: AvgVideoVideo[] = [];
let featuredVideo: AvgVideoVideo | null = null;
let activeRubricId: string | null = null;
let activeSubrubricId: string | null = null;
let allRubrics: AvgVideoRubric[] = [];
let activeSearch = '';
let activeSort = 'newest';
/** Страница экрана «Все видео»: её собирает сервер, клиент только дозагружает. */
let gridItems: AvgVideoVideo[] = [];
let gridTotal = 0;
let gridLoading = false;
/** Номер последнего запроса списка: ответы обгоняют друг друга при быстром наборе. */
let gridRequestSeq = 0;
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let playerModalBound = false;
let hoverPreviewBound = false;
/* Обе точки входа модуля вешают обработчики каталога. Без флага они вешались
   дважды, и каждый клик по вкладке или рубрике уходил на сервер двумя
   одинаковыми запросами. */
let userEventsBound = false;
let activePreviewId: string | null = null;
let hoverPreviewTimer: ReturnType<typeof setTimeout> | null = null;
let currentHoverVideo: HTMLVideoElement | null = null;
let continueItems: AvgVideoProgress[] = [];
let collections: AvgVideoCollection[] = [];
let progressMap: Map<string, AvgVideoProgress> = new Map();
let currentPlayingVideoId: string | null = null;
let lastProgressSendTime: number = 0;
let playbackCache: Map<string, PlaybackCacheEntry> = new Map();

/** Все известные карточки видео по id: список, состав подборок, контекст плеера. */
const videoIndex: Map<string, AvgVideoVideo> = new Map();
const collectionDetails: Map<string, AvgVideoCollectionDetail> = new Map();
const videoContexts: Map<string, AvgVideoContext> = new Map();
const pendingLoads: Set<string> = new Set();

let screen: AtvScreen = 'home';
let entityId: string | null = null;
let activeSeasonId: string | null = null;
/** Общий выбор списка и карты: `video:<id>` | `season:<id>` | `collection:<id>`. */
let selectedKey: string | null = null;

let mapOpen = false;
let mapBound = false;
let mapMode: AvgVideoMapMode = 'series';
let mapVideoId: string | null = null;
let mapCollectionId: string | null = null;
let mapSeasonId: string | null = null;
let mapTopicId: string | null = null;
let mapDetailText = '';

const hoverPreviewEnabled = typeof window !== 'undefined'
  && window.matchMedia('(hover: hover) and (pointer: fine)').matches
  && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function authHeaders(): Record<string, string> {
  return { Authorization: 'Bearer ' + state.authToken };
}

function indexVideos(videos: AvgVideoVideo[]): void {
  for (const video of videos) {
    if (video && typeof video.id === 'string') videoIndex.set(video.id, video);
  }
}

function getVideo(id: string | null | undefined): AvgVideoVideo | null {
  if (!id) return null;
  return videoIndex.get(id) || null;
}

async function resolvePlayback(videoId: string): Promise<PlaybackCacheEntry | null> {
  const now = Date.now();
  const cached = playbackCache.get(videoId);
  if (cached && now - cached.fetchedAt < PLAYBACK_CACHE_TTL_MS) {
    return cached;
  }

  try {
    const r = await fetch(`/api/avg-video/videos/${encodeURIComponent(videoId)}/play`, {
      headers: authHeaders(),
    });

    if (!r.ok) {
      let message = 'Не удалось загрузить видео';
      try {
        const errorData = await r.json() as { message?: string };
        if (errorData.message) {
          message = errorData.message;
        }
      } catch {
        /* ignore JSON parse error */
      }
      showToast('❌ ' + message);
      return null;
    }

    const data = await r.json() as { video_url: string | null; preview_url: string | null };
    const entry: PlaybackCacheEntry = {
      video_url: data.video_url || null,
      preview_url: data.preview_url || null,
      fetchedAt: now,
    };
    playbackCache.set(videoId, entry);
    return entry;
  } catch (err) {
    console.error('Failed to resolve playback:', err);
    showToast('❌ Ошибка при загрузке видео');
    return null;
  }
}

async function loadRubrics(): Promise<void> {
  try {
    const r = await fetch('/api/avg-video/rubrics', { headers: authHeaders() });
    if (!r.ok) throw new Error('Не удалось загрузить рубрики');
    const data = await r.json() as { items?: AvgVideoRubric[] };
    allRubrics = data.items || [];
  } catch (err) {
    console.error('Failed to load rubrics:', err);
    allRubrics = [];
  }
}

async function loadContinueAndCollections(): Promise<void> {
  try {
    const meResp = await fetch('/api/avg-video/me', { headers: authHeaders() });
    if (meResp.ok) {
      const meData = await meResp.json() as {
        continue?: AvgVideoProgress[] | { items?: AvgVideoProgress[] };
      };
      const continueData = meData.continue;
      if (Array.isArray(continueData)) {
        continueItems = continueData;
      } else if (continueData && 'items' in continueData && Array.isArray(continueData.items)) {
        continueItems = continueData.items;
      } else {
        continueItems = [];
      }
      progressMap.clear();
      for (const item of continueItems) {
        progressMap.set(item.video_id, item);
      }
    }
  } catch (err) {
    console.error('Failed to load continue:', err);
    continueItems = [];
  }

  try {
    const colResp = await fetch('/api/avg-video/collections', { headers: authHeaders() });
    if (colResp.ok) {
      const colData = await colResp.json() as { items?: unknown[] };
      collections = (colData.items || [])
        .map(normalizeCollection)
        .filter((c): c is AvgVideoCollection => !!c);
    }
  } catch (err) {
    console.error('Failed to load collections:', err);
    collections = [];
  }
}

/** Состав подборки/сериала. Ответ кешируется: экран и карта берут его из кеша. */
async function loadCollectionDetail(id: string, seasonId?: string | null): Promise<AvgVideoCollectionDetail | null> {
  const key = seasonId ? `col:${id}:${seasonId}` : `col:${id}`;
  if (pendingLoads.has(key)) return collectionDetails.get(id) || null;
  pendingLoads.add(key);

  try {
    const query = seasonId ? `?season=${encodeURIComponent(seasonId)}` : '';
    const r = await fetch(`/api/avg-video/collections/${encodeURIComponent(id)}${query}`, {
      headers: authHeaders(),
    });
    if (!r.ok) throw new Error('Не удалось загрузить подборку');
    const detail = normalizeCollectionDetail(await r.json());
    if (!detail) return collectionDetails.get(id) || null;

    indexVideos(detail.videos);

    const previous = collectionDetails.get(id);
    if (previous && seasonId) {
      // Догрузка состава одного сезона: подмешиваем в уже известный состав.
      const merged = new Map(previous.items.map((i) => [`${i.video_id}:${i.season_id ?? ''}`, i]));
      for (const item of detail.items) merged.set(`${item.video_id}:${item.season_id ?? ''}`, item);
      const collection = detail.collection.seasons?.length ? detail.collection : previous.collection;
      collectionDetails.set(id, { collection, videos: previous.videos, items: [...merged.values()] });
    } else {
      collectionDetails.set(id, detail);
    }
    return collectionDetails.get(id) || null;
  } catch (err) {
    console.error('Failed to load collection:', err);
    return collectionDetails.get(id) || null;
  } finally {
    pendingLoads.delete(key);
  }
}

/** Контекст видео: сериал/сезон/соседи, тематические подборки, связанные материалы. */
async function loadVideoContext(videoId: string): Promise<AvgVideoContext | null> {
  const cached = videoContexts.get(videoId);
  if (cached) return cached;
  const key = `ctx:${videoId}`;
  if (pendingLoads.has(key)) return null;
  pendingLoads.add(key);

  try {
    const r = await fetch(`/api/avg-video/videos/${encodeURIComponent(videoId)}/context`, {
      headers: authHeaders(),
    });
    if (!r.ok) throw new Error('Не удалось загрузить связи видео');
    const context = normalizeVideoContext(await r.json());
    if (context.video) indexVideos([context.video]);
    if (context.series) {
      indexVideos(context.series.episodes);
      if (context.series.prev) indexVideos([context.series.prev]);
      if (context.series.next) indexVideos([context.series.next]);
    }
    for (const topic of context.collections) indexVideos(topic.videos);
    indexVideos(context.related);
    videoContexts.set(videoId, context);
    return context;
  } catch (err) {
    console.error('Failed to load video context:', err);
    return null;
  } finally {
    pendingLoads.delete(key);
  }
}

// ============ Поиск и сортировка внутри открытой сущности ============

/*
 * Состав подборки и сезона фильтруется и сортируется на клиенте осознанно: он
 * мал и уже загружен целиком. Экран «Все видео» так не делает — там страницу
 * собирает сервер (`search` / `sort` / `rubric` / `limit` / `offset`).
 */

function entityMatchesQuery(video: AvgVideoVideo, q: string): boolean {
  if (!q) return true;
  const hay = [
    video.title,
    video.description || '',
    video.annotation || '',
    ...(video.tags || []),
    video.example_label || '',
  ].join(' ').toLowerCase();
  return hay.includes(q);
}

function sortEntityVideos(list: AvgVideoVideo[]): AvgVideoVideo[] {
  const sorted = [...list];
  switch (activeSort) {
    case 'popular':
      sorted.sort((a, b) => b.view_count - a.view_count || (b.published_at || 0) - (a.published_at || 0));
      break;
    case 'order':
      sorted.sort((a, b) => a.sort_order - b.sort_order || (b.published_at || 0) - (a.published_at || 0));
      break;
    case 'short':
      sorted.sort((a, b) => (a.duration_seconds || 99999) - (b.duration_seconds || 99999));
      break;
    case 'newest':
    default:
      sorted.sort((a, b) => (b.published_at || b.created_at) - (a.published_at || a.created_at));
      break;
  }
  return sorted;
}

// ============ Экран «Все видео»: страницы с сервера ============

/** Рубрика для запроса: подрубрика точнее корневой, сервер сам добирает детей. */
function activeRubricParam(): string | null {
  return activeSubrubricId || activeRubricId;
}

function buildListQuery(offset: number): string {
  const params = new URLSearchParams();
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String(offset));
  params.set('sort', activeSort);
  const search = activeSearch.trim();
  if (search) params.set('search', search);
  const rubric = activeRubricParam();
  if (rubric) params.set('rubric', rubric);
  return params.toString();
}

/**
 * Страница общего списка.
 *
 * `append: true` — это «Показать ещё»: следующий кусок приезжает по `offset`, а
 * не отрезается от уже загруженного массива. Ответы нумерованы, потому что при
 * быстром наборе запрос за «психо» может вернуться позже запроса за «психол».
 */
async function loadGridPage(options: { append?: boolean } = {}): Promise<void> {
  const append = options.append === true;
  const offset = append ? gridItems.length : 0;
  const seq = ++gridRequestSeq;
  gridLoading = true;
  if (!append) renderGrid();

  try {
    const r = await fetch(`/api/avg-video/videos?${buildListQuery(offset)}`, { headers: authHeaders() });
    if (!r.ok) throw new Error('Не удалось загрузить видео');
    const data = await r.json() as { items?: AvgVideoVideo[]; total?: number };
    if (seq !== gridRequestSeq) return;

    const items = data.items || [];
    indexVideos(items);
    gridItems = append ? [...gridItems, ...items] : items;
    gridTotal = typeof data.total === 'number' ? data.total : gridItems.length;
  } catch (err) {
    if (seq !== gridRequestSeq) return;
    console.error('Failed to load videos:', err);
    if (!append) {
      gridItems = [];
      gridTotal = 0;
    }
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  } finally {
    if (seq === gridRequestSeq) {
      gridLoading = false;
      renderGrid();
    }
  }
}

/** Смена фильтра, сортировки или запроса — список начинается заново. */
function reloadGrid(): void {
  void loadGridPage();
}

function getFeatured(): AvgVideoVideo | null {
  return homeItems.find((v) => v.is_featured) || homeItems[0] || null;
}

/** Корневая рубрика видео — его направление. Первая из `rubric_ids` без родителя. */
function rootRubricOf(video: AvgVideoVideo): AvgVideoRubric | null {
  for (const id of video.rubric_ids || []) {
    const rubric = allRubrics.find((r) => r.id === id);
    if (rubric && rubric.parent_id === null) return rubric;
  }
  return null;
}

/**
 * Подрубрика видео — если она есть, именно она и есть самое точное про ролик.
 * Предпочитаем ту, что лежит внутри его же направления: у видео может быть
 * несколько привязок, и чужая ветка сбила бы подпись.
 */
function subRubricOf(video: AvgVideoVideo, root: AvgVideoRubric | null): AvgVideoRubric | null {
  const children = (video.rubric_ids || [])
    .map((id) => allRubrics.find((r) => r.id === id))
    .filter((r): r is AvgVideoRubric => !!r && r.parent_id !== null);
  if (children.length === 0) return null;
  return children.find((r) => r.parent_id === root?.id) || children[0];
}

// ============ Общие куски разметки ============

/* Описание на карточке обрезается до двух строк, поэтому разметку снимаем:
   markdown разъезжается в обрезке, а сырые звёздочки читаются как опечатка.
   Полный текст с разметкой показывается в плеере и на экране подборки. */
function renderCardDesc(text: string | null | undefined, className = 'atv-card-desc'): string {
  const desc = markdownToPlain(text);
  if (!desc) return '';
  return `<p class="${className}">${escapeHtml(desc)}</p>`;
}

/** Полный текст с разметкой: аннотации, описания в плеере и на экране подборки. */
function renderRichText(text: string | null | undefined, className: string): string {
  const value = (text || '').trim();
  if (!value) return '';
  return `<div class="${className} atv-md">${renderMarkdownHtml(value)}</div>`;
}

/**
 * Описание под сворачиванием: видны три строки, остальное — по кнопке.
 *
 * Описание сериала бывает в несколько абзацев, и целиком оно занимало экран
 * выше состава — до серий приходилось листать. Кнопку показываем только когда
 * текст действительно не поместился: решает замер после вставки в DOM
 * (`syncCollapsibles`), а не длина строки — та зависит от ширины окна.
 */
function renderCollapsibleRichText(text: string | null | undefined, className: string): string {
  const body = renderRichText(text, `${className} atv-collapse-body`);
  if (!body) return '';
  return `
    <div class="atv-collapse" data-atv-collapsible>
      ${body}
      <button type="button" class="atv-collapse-toggle" data-atv-collapse-toggle aria-expanded="false" hidden>
        Показать полностью
      </button>
    </div>
  `;
}

/** Показать кнопку раскрытия там, где текст не поместился в три строки. */
function syncCollapsibles(scope: HTMLElement | null): void {
  if (!scope) return;
  scope.querySelectorAll<HTMLElement>('[data-atv-collapsible]').forEach((wrap) => {
    const body = wrap.querySelector<HTMLElement>('.atv-collapse-body');
    const toggle = wrap.querySelector<HTMLButtonElement>('[data-atv-collapse-toggle]');
    if (!body || !toggle) return;
    // В раскрытом виде обрезки нет — судить по ней нельзя, кнопка нужна всегда.
    toggle.hidden = !wrap.classList.contains('is-open')
      && body.scrollHeight - body.clientHeight <= 1;
  });
}

function toggleCollapsible(toggle: HTMLElement): void {
  const wrap = toggle.closest<HTMLElement>('[data-atv-collapsible]');
  if (!wrap) return;
  const open = wrap.classList.toggle('is-open');
  toggle.setAttribute('aria-expanded', String(open));
  toggle.textContent = open ? 'Свернуть' : 'Показать полностью';
}

function playIconSvg(size = 20): string {
  return `<svg width="${size}" height="${size}" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>`;
}

function mapIconSvg(size = 16): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="2" y="4" width="7" height="6" rx="1.5"/><rect x="15" y="3" width="7" height="5" rx="1.5"/><rect x="15" y="15" width="7" height="5" rx="1.5"/><path d="M9 7c3 0 3 -1.5 6 -1.5"/><path d="M9 7c3 0 3 10.5 6 10.5"/></svg>`;
}

function renderAnnotationBlock(video: AvgVideoVideo, className = 'atv-anno'): string {
  if (!video.annotation?.trim()) return '';
  return `
    <details class="${className}" data-atv-anno>
      <summary>Аннотация</summary>
      ${renderRichText(video.annotation, 'atv-anno-body')}
    </details>
  `;
}

function progressPercent(videoId: string): number {
  const progress = progressMap.get(videoId);
  if (!progress?.duration_seconds) return 0;
  return Math.max(0, Math.min(100, Math.floor(progress.position_seconds / progress.duration_seconds * 100)));
}

/** Бейдж направления: подпись — название корневой рубрики, цвет — из её id. */
/* Бейдж говорит самое точное, что известно про ролик: подрубрику, если она
   задана, иначе направление. Цвет всегда от направления — так на карточке
   читаются оба уровня сразу, без второй плашки. */
function directionBadge(video: AvgVideoVideo): string {
  const root = rootRubricOf(video);
  const leaf = subRubricOf(video, root) || root;
  // Рубрики нет — бейджа тоже нет: пустая плашка хуже отсутствующей.
  if (!leaf) return '';
  const accent = rubricAccentClass((root || leaf).id);
  const title = leaf === root || !root ? leaf.title : `${root.title} · ${leaf.title}`;
  return `<div class="atv-cat-badge ${accent}" title="${escapeAttr(title)}">${escapeHtml(leaf.title)}</div>`;
}

function directionLabel(video: AvgVideoVideo): string {
  const root = rootRubricOf(video);
  const leaf = subRubricOf(video, root);
  if (root && leaf) return `${root.title} · ${leaf.title}`;
  return (leaf || root)?.title || '';
}

function renderThumb(video: AvgVideoVideo, large = false): string {
  const playSize = large ? 28 : 24;
  const previewAttr = video.access === 'ok' && video.preview_url
    ? ` data-atv-preview="${escapeAttr(video.id)}"`
    : '';
  const posterUrl = posterSrc(video);
  const poster = posterUrl
    ? `<img class="atv-thumb-img" src="${escapeAttr(posterUrl)}" alt="" loading="lazy">`
    : `<div class="atv-thumb-fallback">${playIconSvg(large ? 28 : 20)}</div>`;

  const lockBadge = video.access === 'locked'
    ? '<div class="atv-lock-badge">🔒 По подписке</div>'
    : '';

  const duration = formatDuration(video.duration_seconds);

  return `
    <div class="atv-thumb"${previewAttr}>
      <div class="atv-thumb-static">
        ${poster}
        <div class="atv-play-overlay">${playIconSvg(playSize)}</div>
      </div>
      <div class="atv-thumb-hover" aria-hidden="true"></div>
      ${duration ? `<div class="atv-duration">${escapeHtml(duration)}</div>` : ''}
      ${directionBadge(video)}
      ${lockBadge}
    </div>
  `;
}

function renderVideoCard(video: AvgVideoVideo, options: { annotation?: boolean } = {}): string {
  const percent = progressPercent(video.id);
  const selected = selectedKey === `video:${video.id}` ? ' is-selected' : '';
  return `
    <div class="atv-card${selected}" data-atv-card="${escapeAttr(video.id)}">
      <button type="button" class="atv-card-play" data-atv-play="${escapeAttr(video.id)}" aria-label="Смотреть «${escapeAttr(video.title)}»">
        ${renderThumb(video)}
        ${percent > 0 ? `<div class="atv-progress-bar" style="width:${percent}%"></div>` : ''}
      </button>
      <div class="atv-card-body">
        <h3>${escapeHtml(video.title)}</h3>
        ${renderCardDesc(video.description)}
        ${options.annotation === false ? '' : renderAnnotationBlock(video)}
        <div class="atv-card-footer">
          <span>${escapeHtml(formatVideoDate(video.published_at))}</span>
          <span>👁 ${formatViewCount(video.view_count)}</span>
        </div>
      </div>
    </div>
  `;
}

/** Мозаика из постеров состава — «лицо» подборки или сериала на карточке-входе. */
/**
 * Сколько постеров показать, чтобы сетка заполнилась без дыр.
 *
 * Раскладки заданы под эти числа (см. `.atv-entity-mosaic--*`): семь постеров
 * рисуем как шесть, пять — как четыре. Недобор выглядит хуже, чем ровная
 * мозаика: пустая ячейка читается как поломка, а не как «здесь мало видео».
 */
const MOSAIC_FITS = [8, 6, 4, 3, 2, 1];

function renderEntityMosaic(collection: AvgVideoCollection): string {
  const available = collection.video_ids
    .map((id) => getVideo(id))
    .filter((v): v is AvgVideoVideo => !!v)
    .map((v) => posterSrc(v))
    .filter((src): src is string => !!src);
  const fit = MOSAIC_FITS.find((n) => n <= available.length) || 0;
  const posters = available.slice(0, fit);

  // Мозаика живёт внутри <button>, поэтому собрана из span — блочные теги
  // внутри кнопки невалидны.
  const kind = collection.kind === 'series' ? 'Сериал' : 'Подборка';
  if (posters.length === 0) {
    return `<span class="atv-entity-mosaic atv-entity-mosaic--empty"><span class="atv-entity-kind">${kind}</span>${playIconSvg(24)}</span>`;
  }

  const tiles = posters
    .map((src) => `<span class="atv-entity-tile"><img src="${escapeAttr(src)}" alt="" loading="lazy"></span>`)
    .join('');
  return `<span class="atv-entity-mosaic atv-entity-mosaic--${posters.length}"><span class="atv-entity-kind">${kind}</span>${tiles}</span>`;
}

function renderEntityCard(collection: AvgVideoCollection): string {
  const selected = selectedKey === `collection:${collection.id}` ? ' is-selected' : '';
  return `
    <button type="button" class="atv-entity-card${selected}" data-atv-entity="${escapeAttr(collection.id)}">
      ${renderEntityMosaic(collection)}
      <span class="atv-entity-body">
        <span class="atv-entity-title">${escapeHtml(collection.title)}</span>
        ${markdownToPlain(collection.description)
    ? `<span class="atv-entity-desc">${escapeHtml(markdownToPlain(collection.description))}</span>`
    : '<span class="atv-entity-desc atv-entity-desc--empty">Описание пока не заполнено</span>'}
        <span class="atv-entity-meta">${escapeHtml(collectionCompositionLabel(collection))}</span>
      </span>
    </button>
  `;
}

function renderEmpty(text: string): string {
  return `<div class="atv-empty">${escapeHtml(text)}</div>`;
}

// ============ Экран «Обзор» ============

function renderFeatured(): void {
  const host = $('atv-featured-host');
  if (!host) return;

  if (!featuredVideo) {
    host.innerHTML = '';
    host.classList.add('hidden');
    return;
  }

  host.classList.remove('hidden');
  const v = featuredVideo;
  const direction = directionLabel(v);
  host.innerHTML = `
    <div class="atv-section-label">Рекомендуем начать</div>
    <div class="atv-featured-card">
      <button type="button" class="atv-featured-play" data-atv-play="${escapeAttr(v.id)}">
        <div class="atv-featured-grid">
          ${renderThumb(v, true)}
          <div class="atv-featured-body">
            <div class="atv-meta-row">
              ${direction ? `<span class="atv-tag-pill">${escapeHtml(direction)}</span>` : ''}
              <span class="atv-date">${escapeHtml(formatVideoDate(v.published_at))}</span>
            </div>
            <h2 class="atv-card-title">${escapeHtml(v.title)}</h2>
            ${renderCardDesc(v.description)}
            <div class="atv-stats">
              <span>👁 ${formatViewCount(v.view_count)}</span>
              ${v.example_label ? `<span>После просмотра → ${escapeHtml(v.example_label)}</span>` : ''}
            </div>
          </div>
        </div>
      </button>
      ${renderAnnotationBlock(v, 'atv-anno atv-anno--featured')}
    </div>
  `;
}

function renderContinue(): void {
  const host = $('atv-continue-host');
  if (!host) return;

  const cards = continueItems
    .map((progress) => {
      const video = getVideo(progress.video_id);
      if (!video) return '';
      const percent = progress.duration_seconds
        ? Math.max(0, Math.min(100, Math.floor(progress.position_seconds / progress.duration_seconds * 100)))
        : 0;
      return `
        <div class="atv-card">
          <button type="button" class="atv-card-play" data-atv-play="${escapeAttr(video.id)}" aria-label="Продолжить «${escapeAttr(video.title)}»">
            ${renderThumb(video)}
            <div class="atv-progress-bar" style="width: ${percent}%"></div>
          </button>
          <div class="atv-card-body">
            <h3>${escapeHtml(video.title)}</h3>
            ${renderCardDesc(video.description)}
            <div class="atv-card-footer">
              <span>${escapeHtml(formatVideoDate(video.published_at))}</span>
              <span>👁 ${formatViewCount(video.view_count)}</span>
            </div>
          </div>
        </div>
      `;
    })
    .filter((html) => html)
    .join('');

  if (!cards) {
    host.innerHTML = '';
    host.classList.add('hidden');
    return;
  }

  host.classList.remove('hidden');
  host.innerHTML = `
    <div class="atv-section-label">Продолжить просмотр</div>
    <div class="atv-grid atv-grid--continue">${cards}</div>
  `;
}

function entityGroupHtml(
  title: string,
  hint: string,
  items: AvgVideoCollection[],
  screenId: AtvScreen,
  emptyText: string,
): string {
  const shown = items.slice(0, HOME_ENTITY_LIMIT);
  const rest = items.length - shown.length;
  const cards = shown.map(renderEntityCard).join('');
  return `
    <section class="atv-entry-group">
      <div class="atv-entry-head">
        <div>
          <h3 class="atv-entry-title">${escapeHtml(title)}</h3>
          <p class="atv-entry-hint">${escapeHtml(hint)}</p>
        </div>
        ${items.length > 0
    ? `<button type="button" class="atv-ghost-btn" data-atv-screen="${screenId}">Все · ${items.length}</button>`
    : ''}
      </div>
      ${cards ? `<div class="atv-entity-grid">${cards}</div>` : renderEmpty(emptyText)}
      ${rest > 0
    ? `<div class="atv-entry-more"><button type="button" class="atv-ghost-btn" data-atv-screen="${screenId}">Показать ещё ${rest}</button></div>`
    : ''}
    </section>
  `;
}

function renderEntries(): void {
  const host = $('atv-entries-host');
  if (!host) return;

  const thematic = collections.filter((c) => c.kind !== 'series');
  const series = collections.filter((c) => c.kind === 'series');

  host.innerHTML = entityGroupHtml(
    'Тематические подборки',
    'Материалы одной темы — без обязательного порядка.',
    thematic,
    'collections',
    'Подборки пока не собраны.',
  ) + entityGroupHtml(
    'Сериалы',
    'Последовательные курсы: сезоны и серии по порядку.',
    series,
    'series',
    'Сериалы пока не собраны.',
  );
}

// ============ Экран списка сущностей ============

function renderBrowse(): void {
  const host = $('atv-browse-host');
  if (!host) return;

  const isSeries = screen === 'series';
  const q = activeSearch.trim().toLowerCase();
  const items = collections
    .filter((c) => (isSeries ? c.kind === 'series' : c.kind !== 'series'))
    .filter((c) => !q || `${c.title} ${c.description || ''}`.toLowerCase().includes(q));

  const title = isSeries ? 'Сериалы' : 'Тематические подборки';
  const hint = isSeries
    ? 'Сериал — упорядоченная структура: сезон и номер серии задают путь.'
    : 'Подборка объединяет материалы одной темы; видео может входить в несколько подборок.';

  host.innerHTML = `
    <div class="atv-entry-head">
      <div>
        <h3 class="atv-entry-title">${escapeHtml(title)}</h3>
        <p class="atv-entry-hint">${escapeHtml(hint)}</p>
      </div>
    </div>
    ${items.length > 0
    ? `<div class="atv-entity-grid">${items.map(renderEntityCard).join('')}</div>`
    : renderEmpty(q ? 'Ничего не найдено. Попробуйте другой запрос.' : 'Здесь пока пусто.')}
  `;
}

// ============ Экран подборки / сериала ============

interface SeriesView {
  seasons: AvgVideoSeason[];
  seasonId: string | null;
  episodes: AvgVideoVideo[];
}

function buildSeriesView(detail: AvgVideoCollectionDetail, seasonId: string | null): SeriesView {
  const seasons = [...(detail.collection.seasons || [])].sort((a, b) => (a.number || 0) - (b.number || 0));
  const hasSeasons = seasons.length > 0;
  const selected = hasSeasons
    ? (seasons.find((s) => s.id === seasonId)?.id || seasons[0].id)
    : null;

  const items = [...detail.items]
    .filter((item) => (hasSeasons ? item.season_id === selected : true))
    .sort((a, b) => a.position - b.position);

  const episodes = items
    .map((item) => getVideo(item.video_id))
    .filter((v): v is AvgVideoVideo => !!v);

  return { seasons, seasonId: selected, episodes };
}

function renderEpisodeRow(video: AvgVideoVideo, index: number): string {
  const posterUrl = posterSrc(video);
  const percent = progressPercent(video.id);
  const selected = selectedKey === `video:${video.id}` ? ' is-selected' : '';
  const number = episodeNumber(video, index);
  const meta = [
    formatDuration(video.duration_seconds),
    `👁 ${formatViewCount(video.view_count)}`,
    video.access === 'locked' ? '🔒 По подписке' : '',
  ].filter(Boolean).join(' · ');

  return `
    <li class="atv-episode${selected}" data-atv-episode="${escapeAttr(video.id)}">
      <button type="button" class="atv-episode-play" data-atv-play="${escapeAttr(video.id)}">
        <span class="atv-episode-num">${escapeHtml(String(number))}</span>
        <span class="atv-episode-thumb"${video.access === 'ok' && video.preview_url ? ` data-atv-preview="${escapeAttr(video.id)}"` : ''}>
          ${posterUrl
    ? `<img src="${escapeAttr(posterUrl)}" alt="" loading="lazy">`
    : `<span class="atv-episode-thumb-empty">${playIconSvg(18)}</span>`}
          ${percent > 0 ? `<span class="atv-progress-bar" style="width:${percent}%"></span>` : ''}
        </span>
        <span class="atv-episode-info">
          <span class="atv-episode-title">${escapeHtml(video.title)}</span>
          <span class="atv-episode-meta">${escapeHtml(meta)}</span>
          ${markdownToPlain(video.description) ? `<span class="atv-episode-desc">${escapeHtml(markdownToPlain(video.description))}</span>` : ''}
        </span>
      </button>
      <button type="button" class="atv-icon-ghost" data-atv-map-open="video:${escapeAttr(video.id)}" aria-label="Карта серии «${escapeAttr(video.title)}»" title="Карта">
        ${mapIconSvg(16)}
      </button>
    </li>
  `;
}

function renderEntityScreen(): void {
  const host = $('atv-entity-host');
  if (!host) return;

  if (!entityId) {
    host.innerHTML = renderEmpty('Подборка не выбрана.');
    return;
  }

  const detail = collectionDetails.get(entityId);
  const summary = detail?.collection || collections.find((c) => c.id === entityId) || null;
  if (!summary) {
    host.innerHTML = renderEmpty('Загрузка подборки…');
    return;
  }

  const isSeries = summary.kind === 'series';
  const q = activeSearch.trim().toLowerCase();

  const head = `
    <div class="atv-entity-head">
      <div class="atv-entity-head-main">
        <span class="atv-entity-kicker">${isSeries ? 'Сериал' : 'Тематическая подборка'}</span>
        <h2 class="atv-entity-headline">${escapeHtml(summary.title)}</h2>
        ${renderCollapsibleRichText(summary.description, 'atv-entity-lead')}
        <div class="atv-entity-meta-row">
          <span class="atv-tag-pill atv-tag-pill--muted">${escapeHtml(collectionCompositionLabel(summary))}</span>
          ${q ? `<span class="atv-date">Поиск внутри: «${escapeHtml(activeSearch.trim())}»</span>` : ''}
        </div>
      </div>
      <div class="atv-entity-actions">
        <button type="button" class="atv-ghost-btn" data-atv-map-open="collection:${escapeAttr(summary.id)}">
          ${mapIconSvg(16)} Карта
        </button>
      </div>
    </div>
  `;

  if (!detail) {
    host.innerHTML = head + renderEmpty('Загрузка состава…');
    syncCollapsibles(host);
    return;
  }

  if (isSeries) {
    const view = buildSeriesView(detail, activeSeasonId);
    activeSeasonId = view.seasonId;
    const episodes = view.episodes.filter((v) => entityMatchesQuery(v, q));

    const seasonPills = view.seasons.length > 0
      ? `<div class="atv-seasons" role="group" aria-label="Сезоны сериала">${view.seasons.map((season) => `
          <button type="button" class="atv-season-pill${season.id === view.seasonId ? ' active' : ''}" data-atv-season="${escapeAttr(season.id)}" aria-pressed="${season.id === view.seasonId}">
            <span class="atv-season-name">${escapeHtml(seasonLabel(season))}</span>
            <span class="atv-season-count">${season.item_count} ${plural(season.item_count, 'серия', 'серии', 'серий')}</span>
          </button>
        `).join('')}</div>`
      // Сериал без сезонов — валидный случай: плоский список серий.
      : '<div class="atv-season-flat">Сериал идёт одним сезоном — серии по порядку.</div>';

    // Состав сезона у большого сериала приезжает отдельным запросом: пока он в
    // пути, честнее показать загрузку, чем «серий нет».
    const seasonKnown = view.seasons.length === 0
      || detail.items.some((item) => item.season_id === view.seasonId);
    const emptyText = !seasonKnown
      ? 'Загрузка серий сезона…'
      : (q ? 'В этом сезоне ничего не найдено.' : 'В этом сезоне пока нет серий.');

    host.innerHTML = head + seasonPills + (episodes.length > 0
      ? `<ol class="atv-episodes">${episodes.map(renderEpisodeRow).join('')}</ol>`
      : renderEmpty(emptyText));
    syncCollapsibles(host);
    return;
  }

  const videos = sortEntityVideos(
    detail.items
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((item) => getVideo(item.video_id))
      .filter((v): v is AvgVideoVideo => !!v),
  ).filter((v) => entityMatchesQuery(v, q));

  host.innerHTML = head + (videos.length > 0
    ? `<div class="atv-grid">${videos.map((v) => renderVideoCard(v)).join('')}</div>`
    : renderEmpty(q ? 'В этой подборке ничего не найдено.' : 'В подборке пока нет видео.'));
  syncCollapsibles(host);
}

// ============ Экран «Все видео» ============

/**
 * Разбивка списка по подрубрикам выбранного направления.
 *
 * Включается только там, где она что-то объясняет: выбрано направление, у него
 * есть дети и конкретная подрубрика ещё не выбрана. Группируем то, что уже
 * загружено: список приходит страницами, и на «Показать ещё» группы просто
 * дорастают. `null` — группировки нет, рисуем обычной сеткой.
 */
function groupGridBySubrubric(): Array<{ title: string; videos: AvgVideoVideo[] }> | null {
  if (!activeRubricId || activeSubrubricId) return null;

  const children = allRubrics
    .filter((r) => r.parent_id === activeRubricId && r.published_count > 0)
    .sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title, 'ru'));
  if (children.length === 0) return null;

  const groups = children.map((child) => ({
    title: child.title,
    videos: gridItems.filter((v) => (v.rubric_ids || []).includes(child.id)),
  }));

  // Видео прямо в направлении, мимо подрубрик, — отдельной группой в конце.
  const childIds = new Set(children.map((c) => c.id));
  const rest = gridItems.filter((v) => !(v.rubric_ids || []).some((id) => childIds.has(id)));
  if (rest.length > 0) groups.push({ title: 'Остальное', videos: rest });

  const filled = groups.filter((g) => g.videos.length > 0);
  // Одна группа объясняет не больше, чем простой список.
  return filled.length > 1 ? filled : null;
}

function renderGrid(): void {
  const grid = $('atv-video-grid');
  const loadWrap = $('atv-load-more-wrap');
  const loadBtn = $('atv-load-more') as HTMLButtonElement | null;
  if (!grid) return;

  if (gridItems.length === 0) {
    grid.innerHTML = renderEmpty(gridLoading
      ? 'Загрузка видео…'
      : 'Видео не найдены. Попробуйте другой фильтр или поиск.');
    loadWrap?.classList.add('hidden');
    return;
  }

  const groups = groupGridBySubrubric();
  grid.innerHTML = groups
    ? groups.map((g) => `
      <section class="atv-grid-group">
        <h4 class="atv-grid-group-title">${escapeHtml(g.title)}<span class="atv-grid-group-count">${g.videos.length}</span></h4>
        <div class="atv-grid">${g.videos.map((v) => renderVideoCard(v)).join('')}</div>
      </section>
    `).join('')
    : gridItems.map((v) => renderVideoCard(v)).join('');
  // Сгруппированный список сам раскладывает сетки внутри секций.
  grid.classList.toggle('atv-grid', !groups);
  loadWrap?.classList.toggle('hidden', gridItems.length >= gridTotal);
  if (loadBtn) {
    loadBtn.disabled = gridLoading;
    loadBtn.textContent = gridLoading
      ? 'Загрузка…'
      : `Показать ещё · осталось ${gridTotal - gridItems.length}`;
  }
}

function renderRubrics(): void {
  const host = $('atv-categories');
  if (!host) return;

  const rootRubrics = allRubrics.filter((r) => !r.parent_id && r.published_count > 0);
  const pills: Array<{ id: string; title: string }> = [{ id: 'all', title: 'Все' }, ...rootRubrics];

  host.innerHTML = pills.map((r) => `
    <button type="button" class="atv-pill${(activeRubricId || 'all') === r.id ? ' active' : ''}" data-atv-rubric="${escapeAttr(r.id)}">
      ${escapeHtml(r.title)}
    </button>
  `).join('');
}

function renderSubrubrics(): void {
  const host = $('atv-subrubrics');
  if (!host) return;

  if (!activeRubricId || activeRubricId === 'all') {
    host.style.display = 'none';
    return;
  }

  const childRubrics = allRubrics.filter((r) => r.parent_id === activeRubricId && r.published_count > 0);
  if (childRubrics.length === 0) {
    host.style.display = 'none';
    return;
  }

  host.style.display = 'flex';
  host.innerHTML = childRubrics.map((r) => `
    <button type="button" class="atv-pill${activeSubrubricId === r.id ? ' active' : ''}" data-atv-subrubric="${escapeAttr(r.id)}">
      ${escapeHtml(r.title)}
    </button>
  `).join('');
}

// ============ Каркас: вкладки, крошки, тулбар ============

function currentEntity(): AvgVideoCollection | null {
  if (!entityId) return null;
  return collectionDetails.get(entityId)?.collection || collections.find((c) => c.id === entityId) || null;
}

function tabForScreen(): AtvScreen {
  if (screen !== 'entity') return screen;
  return currentEntity()?.kind === 'series' ? 'series' : 'collections';
}

function renderTabs(): void {
  const active = tabForScreen();
  document.querySelectorAll<HTMLButtonElement>('#atv-tabs [data-atv-screen]').forEach((btn) => {
    const isActive = btn.dataset.atvScreen === active;
    btn.classList.toggle('active', isActive);
    if (isActive) btn.setAttribute('aria-current', 'page');
    else btn.removeAttribute('aria-current');
  });
}

function renderCrumbs(): void {
  const host = $('atv-crumbs');
  if (!host) return;

  if (screen === 'home') {
    host.classList.add('hidden');
    host.innerHTML = '';
    return;
  }

  const parts: string[] = ['<button type="button" class="atv-crumb" data-atv-screen="home">Каталог</button>'];
  if (screen === 'collections' || screen === 'series' || screen === 'entity') {
    const entity = currentEntity();
    const kindScreen: AtvScreen = screen === 'entity'
      ? (entity?.kind === 'series' ? 'series' : 'collections')
      : screen;
    parts.push(`<button type="button" class="atv-crumb" data-atv-screen="${kindScreen}">${kindScreen === 'series' ? 'Сериалы' : 'Тематические подборки'}</button>`);
    if (screen === 'entity' && entity) {
      parts.push(`<span class="atv-crumb atv-crumb--current">${escapeHtml(entity.title)}</span>`);
    }
  } else if (screen === 'all') {
    parts.push('<span class="atv-crumb atv-crumb--current">Все видео</span>');
  }

  host.classList.remove('hidden');
  host.innerHTML = parts.join('<span class="atv-crumb-sep" aria-hidden="true">/</span>');
}

function renderToolbar(): void {
  const entity = screen === 'entity' ? currentEntity() : null;
  const search = $('atv-search') as HTMLInputElement | null;
  if (search) {
    search.placeholder = entity ? `Поиск внутри «${entity.title}»` : 'Найти видео во всём каталоге…';
    if (search.value !== activeSearch) search.value = activeSearch;
  }

  const sort = $('atv-sort');
  const sortVisible = screen === 'all' || (screen === 'entity' && entity?.kind !== 'series');
  sort?.classList.toggle('hidden', !sortVisible);

  const mapBtn = $('atv-map-btn') as HTMLButtonElement | null;
  if (mapBtn) {
    const target = resolveMapTarget();
    mapBtn.disabled = !target.videoId && !target.collectionId;
  }
}

function showScreen(): void {
  const map: Record<string, AtvScreen[]> = {
    'atv-screen-home': ['home'],
    'atv-screen-browse': ['collections', 'series'],
    'atv-screen-entity': ['entity'],
    'atv-screen-all': ['all'],
  };
  for (const [id, screens] of Object.entries(map)) {
    $(id)?.classList.toggle('hidden', !screens.includes(screen));
  }
}

/**
 * Подтягивает выбранный объект в видимую часть списка.
 *
 * Вторая половина синхронизации со списком: выбор узла на карте не только
 * подсвечивает карточку, но и доводит её до глаз — иначе после закрытия карты
 * пользователь смотрит на прежнее место списка.
 */
function revealSelected(): void {
  if (!selectedKey?.startsWith('video:')) return;
  const id = selectedKey.slice('video:'.length);
  const node = document.querySelector<HTMLElement>(`[data-atv-episode="${CSS.escape(id)}"]`)
    || document.querySelector<HTMLElement>(`[data-atv-card="${CSS.escape(id)}"]`);
  node?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
}

function renderAll(): void {
  featuredVideo = getFeatured();
  renderTabs();
  renderCrumbs();
  renderToolbar();
  showScreen();

  if (screen === 'home') {
    renderContinue();
    renderFeatured();
    renderEntries();
  } else if (screen === 'collections' || screen === 'series') {
    renderBrowse();
  } else if (screen === 'entity') {
    renderEntityScreen();
  } else {
    renderRubrics();
    renderSubrubrics();
    renderGrid();
  }

  revealSelected();
}

// ============ Навигация ============

function goScreen(next: AtvScreen): void {
  screen = next;
  if (next !== 'entity') {
    entityId = null;
    activeSeasonId = null;
  }
  stopHoverPreview();
  renderAll();
  // Общий список приходит с сервера: возвращаясь на экран, спрашиваем его
  // заново — фильтр, сортировка или запрос могли смениться на других экранах.
  if (next === 'all') reloadGrid();
  $('atv-video-root')?.scrollTo({ top: 0, behavior: 'auto' });
}

async function openEntity(id: string, seasonId?: string | null): Promise<void> {
  entityId = id;
  screen = 'entity';
  activeSeasonId = seasonId ?? null;
  selectedKey = `collection:${id}`;
  renderAll();

  const detail = await loadCollectionDetail(id);
  if (detail && entityId === id) {
    await ensureSeasonComposition(detail, activeSeasonId);
    renderAll();
  }
}

/**
 * Догружает состав выбранного сезона.
 *
 * Базовый ответ большого сериала по ADR может нести только счётчики; тогда
 * состав сезона приезжает отдельным запросом `?season=<id>`.
 */
async function ensureSeasonComposition(detail: AvgVideoCollectionDetail, seasonId: string | null): Promise<void> {
  if (detail.collection.kind !== 'series') return;
  const seasons = detail.collection.seasons || [];
  if (seasons.length === 0) return;
  const target = seasons.find((s) => s.id === seasonId) || seasons[0];
  if (!target) return;
  const known = detail.items.some((item) => item.season_id === target.id);
  if (known) return;
  await loadCollectionDetail(detail.collection.id, target.id);
}

async function selectSeason(seasonId: string): Promise<void> {
  activeSeasonId = seasonId;
  selectedKey = `season:${seasonId}`;
  mapSeasonId = seasonId;
  renderAll();
  const detail = entityId ? collectionDetails.get(entityId) : null;
  if (detail) {
    await ensureSeasonComposition(detail, seasonId);
    renderAll();
  }
  if (mapOpen) renderMap();
}

// ============ Плеер ============

function buildPlayerContent(video: AvgVideoVideo): string {
  const url = video.video_url?.trim();
  if (!url) {
    return '<div class="atv-player-placeholder">Ссылка на видео не указана. Обратитесь к администратору.</div>';
  }

  const resolved = resolveVideoEmbedSrc(url);
  if (!resolved) {
    return '<div class="atv-player-placeholder">Ссылка VK не распознана. В админке вставьте адрес vkvideo.ru или код iframe из «Экспорт» на VK.</div>';
  }

  if (resolved.kind === 'file') {
    return `<div class="atv-player-wrap"><video controls src="${escapeAttr(resolved.embedSrc)}" title="${escapeAttr(video.title)}"></video></div>`;
  }

  const allow = resolved.kind === 'vk'
    ? 'autoplay; encrypted-media; fullscreen; picture-in-picture'
    : 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';

  const iframe = `<iframe src="${escapeAttr(resolved.embedSrc)}" allow="${allow}" allowfullscreen referrerpolicy="strict-origin-when-cross-origin" title="${escapeAttr(video.title)}"></iframe>`;

  if (resolved.kind === 'vk') {
    const hint = resolved.needsVkHash
      ? '<p class="atv-vk-hint">Встроенный плеер VK иногда требует код «Экспорт» в админке. Пока можно смотреть по кнопке ниже.</p>'
      : '';
    return `
      <div class="atv-vk-fallback">
        ${hint}
        <a class="atv-cta-btn atv-vk-open-btn" href="${escapeAttr(resolved.watchUrl)}" target="_blank" rel="noopener noreferrer">▶ Смотреть на VK</a>
      </div>
      <div class="atv-player-wrap">${iframe}</div>
    `;
  }

  return `<div class="atv-player-wrap">${iframe}</div>`;
}

async function sendProgress(videoId: string, positionSeconds: number, durationSeconds: number | null): Promise<void> {
  try {
    await fetch(`/api/avg-video/videos/${encodeURIComponent(videoId)}/progress`, {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        position_seconds: Math.floor(positionSeconds),
        ...(durationSeconds != null && { duration_seconds: Math.floor(durationSeconds) }),
      }),
    });
  } catch (err) {
    console.warn('Failed to send progress:', err);
  }
}

function navButton(video: AvgVideoVideo | null, direction: 'prev' | 'next'): string {
  const label = direction === 'prev' ? '← Предыдущая' : 'Следующая →';
  if (!video) {
    return `<span class="atv-nav-btn atv-nav-btn--off" aria-hidden="true">${label}</span>`;
  }
  const lock = video.access === 'locked' ? ' 🔒' : '';
  return `<button type="button" class="atv-nav-btn" data-atv-play="${escapeAttr(video.id)}" title="${escapeAttr(video.title)}">${label}${lock}</button>`;
}

/** Навигация плеера: соседние серии и возврат к сериалу и сезону. */
function renderPlayerNav(videoId: string): void {
  const host = $('atv-player-nav');
  if (!host) return;

  const context = videoContexts.get(videoId);
  const series = context?.series || null;

  const contextLinks: string[] = [];
  if (series?.collection) {
    contextLinks.push(`<button type="button" class="atv-link-btn" data-atv-entity="${escapeAttr(series.collection.id)}">Сериал «${escapeHtml(series.collection.title)}»</button>`);
    if (series.season) {
      contextLinks.push(`<button type="button" class="atv-link-btn" data-atv-season-jump="${escapeAttr(series.collection.id)}:${escapeAttr(series.season.id)}">${escapeHtml(seasonLabel(series.season))}</button>`);
    }
  }

  const mapBtn = `<button type="button" class="atv-nav-btn atv-nav-btn--map" data-atv-map-open="video:${escapeAttr(videoId)}">${mapIconSvg(15)} Карта</button>`;

  if (!series && contextLinks.length === 0) {
    host.innerHTML = `<div class="atv-player-nav-row"><span class="atv-player-nav-spacer"></span>${mapBtn}</div>`;
    return;
  }

  host.innerHTML = `
    <div class="atv-player-nav-row">
      ${navButton(series?.prev || null, 'prev')}
      <div class="atv-player-context">${contextLinks.join('<span class="atv-crumb-sep" aria-hidden="true">/</span>')}</div>
      ${mapBtn}
      ${navButton(series?.next || null, 'next')}
    </div>
  `;
}

async function openPlayer(videoId: string): Promise<void> {
  stopHoverPreview();
  let video = getVideo(videoId);
  if (!video) {
    const context = await loadVideoContext(videoId);
    video = context?.video || null;
  }
  if (!video) return;

  if (video.access === 'locked') {
    showToast('🔒 Видео доступно по подписке');
    return;
  }

  // Ссылки Диска (disk:/… и публичные «Поделиться») раскрывает сервер
  if (needsServerPlayback(video.video_url)) {
    const resolved = await resolvePlayback(videoId);
    if (!resolved) return;
    video.video_url = resolved.video_url;
  }

  const modal = $('atv-player-modal');
  const titleEl = $('atv-player-title');
  const bodyEl = $('atv-player-body');
  const metaEl = $('atv-player-meta');
  if (!modal || !titleEl || !bodyEl) return;

  selectedKey = `video:${videoId}`;
  titleEl.textContent = video.title;
  bodyEl.innerHTML = buildPlayerContent(video);
  renderPlayerNav(videoId);
  if (metaEl) {
    // В плеере текст показан целиком — здесь разметка рисуется, а не снимается.
    const anno = video.annotation?.trim()
      ? `<div class="atv-player-anno"><div class="atv-player-anno-label">Аннотация</div>${renderRichText(video.annotation, 'atv-anno-body')}</div>`
      : '';
    const desc = renderCollapsibleRichText(video.description, 'atv-card-desc--player');

    // Кнопки «Перейти к маршруту» здесь больше нет: открыть КОНКРЕТНЫЙ маршрут
    // экран «Примеры» не умеет, а общий переход уже висит кнопкой внизу
    // каталога. Осталась текстовая подпись маршрута — она несёт смысл.
    const direction = directionLabel(video);
    const duration = formatDuration(video.duration_seconds);

    metaEl.innerHTML = `
      ${desc}
      ${anno}
      <div class="atv-stats" style="margin-top:8px">
        ${direction ? `<span class="atv-tag-pill">${escapeHtml(direction)}</span>` : ''}
        <span>${escapeHtml(formatVideoDate(video.published_at))}</span>
        ${duration ? `<span>${escapeHtml(duration)}</span>` : ''}
        <span>👁 ${formatViewCount(video.view_count)}</span>
        ${video.example_label ? `<span>После просмотра → ${escapeHtml(video.example_label)}</span>` : ''}
      </div>
    `;
    syncCollapsibles(metaEl);
  }

  modal.classList.remove('hidden');
  document.body.style.overflow = 'hidden';
  currentPlayingVideoId = videoId;
  lastProgressSendTime = 0;

  // Контекст догружается фоном: соседние серии и возврат к сезону появляются,
  // как только приедет ответ, — плеер их не ждёт.
  void loadVideoContext(videoId).then(() => {
    if (currentPlayingVideoId === videoId) renderPlayerNav(videoId);
    if (mapOpen && mapVideoId === videoId) renderMap();
  });

  try {
    await fetch(`/api/avg-video/videos/${encodeURIComponent(videoId)}/view`, {
      method: 'POST',
      headers: authHeaders(),
    });
    video.view_count += 1;
    renderAll();
  } catch {
    /* ignore */
  }

  // Setup video element handlers for progress tracking
  setTimeout(() => {
    const videoEl = bodyEl.querySelector<HTMLVideoElement>('video');
    if (!videoEl) return;

    // Restore position from progress
    const progress = progressMap.get(videoId);
    videoEl.addEventListener('loadedmetadata', () => {
      if (progress && progress.position_seconds > 0) {
        videoEl.currentTime = progress.position_seconds;
      }
    }, { once: true });

    // Send progress on timeupdate (throttled)
    videoEl.addEventListener('timeupdate', () => {
      const now = Date.now();
      if (now - lastProgressSendTime >= PROGRESS_THROTTLE_MS) {
        lastProgressSendTime = now;
        void sendProgress(videoId, videoEl.currentTime, videoEl.duration);
      }
    });

    // Send progress on pause
    videoEl.addEventListener('pause', () => {
      void sendProgress(videoId, videoEl.currentTime, videoEl.duration);
    });
  }, 0);
}

function closePlayer(): void {
  const modal = $('atv-player-modal');
  const bodyEl = $('atv-player-body');
  stopHoverPreview();

  // Send progress on close
  if (currentPlayingVideoId) {
    const videoEl = bodyEl?.querySelector<HTMLVideoElement>('video');
    if (videoEl) {
      void sendProgress(currentPlayingVideoId, videoEl.currentTime, videoEl.duration);
    }
    currentPlayingVideoId = null;
  }

  if (bodyEl) bodyEl.innerHTML = '';
  const nav = $('atv-player-nav');
  if (nav) nav.innerHTML = '';
  modal?.classList.add('hidden');
  if (!mapOpen) document.body.style.overflow = '';
}

// ============ Карта ============

function resolveMapTarget(): { videoId: string | null; collectionId: string | null } {
  if (screen === 'entity' && entityId) {
    return { videoId: currentPlayingVideoId || null, collectionId: entityId };
  }
  const videoId = currentPlayingVideoId
    || (selectedKey?.startsWith('video:') ? selectedKey.slice('video:'.length) : null)
    || continueItems[0]?.video_id
    || featuredVideo?.id
    || null;
  return { videoId, collectionId: null };
}

function seriesSourceCollectionId(): string | null {
  const context = mapVideoId ? videoContexts.get(mapVideoId) : null;
  if (context?.series?.collection) return context.series.collection.id;
  if (mapCollectionId) {
    const collection = collectionDetails.get(mapCollectionId)?.collection
      || collections.find((c) => c.id === mapCollectionId);
    if (collection?.kind === 'series') return collection.id;
  }
  const video = getVideo(mapVideoId);
  return video?.series_id || null;
}

function buildMapGraph(): MapGraph | null {
  if (mapMode === 'series') {
    const collectionId = seriesSourceCollectionId();
    const detail = collectionId ? collectionDetails.get(collectionId) : null;
    const context = mapVideoId ? videoContexts.get(mapVideoId) : null;

    // Состав берём из подборки, если он уже загружен: там есть все сезоны и
    // позиции. Контекст видео — запасной источник (несёт текущий сезон).
    if (detail && detail.collection.kind === 'series') {
      const view = buildSeriesView(detail, mapSeasonId || context?.series?.season?.id || activeSeasonId);
      return buildSeriesGraph({
        collection: detail.collection,
        seasons: view.seasons,
        currentSeasonId: view.seasonId,
        episodes: view.episodes,
        currentVideoId: mapVideoId,
      });
    }

    if (context?.series) {
      return buildSeriesGraph({
        collection: context.series.collection,
        seasons: context.series.seasons,
        currentSeasonId: mapSeasonId || context.series.season?.id || null,
        episodes: context.series.episodes,
        currentVideoId: mapVideoId,
      });
    }
    return null;
  }

  const context = mapVideoId ? videoContexts.get(mapVideoId) : null;
  const video = getVideo(mapVideoId);
  if (!context && !video) return null;
  return buildTopicsGraph({
    video: context?.video || video,
    topics: context?.collections || [],
    selectedCollectionId: mapTopicId,
  });
}

function seriesModeAvailable(): boolean {
  return !!seriesSourceCollectionId();
}

function topicsModeAvailable(): boolean {
  return !!mapVideoId;
}

function renderMap(): void {
  const canvas = $('atv-map-canvas');
  const detailEl = $('atv-map-detail');
  const titleEl = $('atv-map-title');
  if (!canvas) return;

  const seriesBtn = $('atv-map-mode-series') as HTMLButtonElement | null;
  const topicsBtn = $('atv-map-mode-topics') as HTMLButtonElement | null;
  if (seriesBtn) {
    seriesBtn.disabled = !seriesModeAvailable();
    seriesBtn.setAttribute('aria-pressed', String(mapMode === 'series'));
    seriesBtn.classList.toggle('active', mapMode === 'series');
  }
  if (topicsBtn) {
    topicsBtn.disabled = !topicsModeAvailable();
    topicsBtn.setAttribute('aria-pressed', String(mapMode === 'topics'));
    topicsBtn.classList.toggle('active', mapMode === 'topics');
  }

  if (titleEl) {
    titleEl.textContent = mapMode === 'series' ? 'В сериале' : 'По темам';
  }

  const graph = buildMapGraph();
  if (!graph) {
    const message = mapMode === 'series'
      ? 'Это видео не входит ни в один сериал — посмотрите режим «По темам».'
      : (mapVideoId ? 'Загрузка связей…' : 'Выберите видео внутри подборки — карта тем строится вокруг него.');
    canvas.innerHTML = `<div class="atv-map-empty">${escapeHtml(message)}</div>`;
    if (detailEl) detailEl.textContent = '';
    return;
  }

  canvas.innerHTML = renderMapSvg(graph);
  /* Панель подгоняется под ширину самого графа: узкая карта не растягивает
     модалку на весь экран, широкая упирается в его границы (остальное делает
     CSS). Запас — на поля панели и рамку холста. */
  const svgEl = canvas.querySelector('svg');
  const panel = document.querySelector<HTMLElement>('.atv-map-panel');
  if (panel) {
    const graphWidth = svgEl?.viewBox?.baseVal?.width || 0;
    if (graphWidth > 0) panel.style.setProperty('--atv-map-w', `${Math.round(graphWidth) + 96}px`);
    else panel.style.removeProperty('--atv-map-w');
  }
  if (detailEl) detailEl.textContent = mapDetailText || graph.caption;
}

async function ensureMapData(): Promise<void> {
  if (mapVideoId) {
    await loadVideoContext(mapVideoId);
  }
  const collectionId = seriesSourceCollectionId();
  if (mapMode === 'series' && collectionId && !collectionDetails.get(collectionId)) {
    const detail = await loadCollectionDetail(collectionId);
    if (detail) {
      const context = mapVideoId ? videoContexts.get(mapVideoId) : null;
      await ensureSeasonComposition(detail, mapSeasonId || context?.series?.season?.id || null);
    }
  }
}

async function openMap(target: { videoId?: string | null; collectionId?: string | null }): Promise<void> {
  const modal = $('atv-map-modal');
  if (!modal) return;

  if (target.videoId !== undefined) mapVideoId = target.videoId;
  if (target.collectionId !== undefined) mapCollectionId = target.collectionId;
  if (!mapVideoId && !mapCollectionId) {
    showToast('Выберите видео или подборку — карта строится вокруг них');
    return;
  }

  mapDetailText = '';
  mapSeasonId = mapSeasonId || activeSeasonId;
  mapTopicId = mapCollectionId && collections.find((c) => c.id === mapCollectionId)?.kind !== 'series'
    ? mapCollectionId
    : mapTopicId;

  mapOpen = true;
  modal.classList.remove('hidden');
  document.body.style.overflow = 'hidden';
  renderMap();

  await ensureMapData();
  if (!mapOpen) return;
  if (mapMode === 'series' && !seriesModeAvailable() && topicsModeAvailable()) {
    mapMode = 'topics';
  } else if (mapMode === 'topics' && !topicsModeAvailable() && seriesModeAvailable()) {
    mapMode = 'series';
  }
  renderMap();
}

function closeMap(): void {
  mapOpen = false;
  $('atv-map-modal')?.classList.add('hidden');
  if (!currentPlayingVideoId) document.body.style.overflow = '';
}

/** Клик по узлу карты меняет контекст: и в карте, и в списке под ней. */
function handleMapNode(key: string, label: string): void {
  mapDetailText = label;
  const [type, ...rest] = key.split(':');
  const id = rest.join(':');

  if (type === 'series') {
    if (!id) return;
    mapCollectionId = id;
    mapSeasonId = null;
    selectedKey = `collection:${id}`;
    void openEntity(id);
    void ensureMapData().then(renderMap);
    renderMap();
    return;
  }

  if (type === 'season') {
    mapSeasonId = id;
    selectedKey = `season:${id}`;
    const collectionId = seriesSourceCollectionId();
    if (collectionId && entityId !== collectionId) {
      void openEntity(collectionId, id);
    } else {
      void selectSeason(id);
    }
    const detail = collectionId ? collectionDetails.get(collectionId) : null;
    if (detail) void ensureSeasonComposition(detail, id).then(renderMap);
    renderMap();
    return;
  }

  if (type === 'video') {
    if (!id) return;
    mapVideoId = id;
    selectedKey = `video:${id}`;
    const video = getVideo(id);
    if (video?.season_id) mapSeasonId = video.season_id;
    /* Узел видео — это само видео, а не контекст: открываем плеер тем же путём,
       что и карточка в списке. Карту закрываем, иначе плеер уедет под неё —
       модалка карты лежит выше по слою. */
    closeMap();
    renderAll();
    void openPlayer(id);
    return;
  }

  if (type === 'collection') {
    if (!id) return;
    mapTopicId = mapTopicId === id ? null : id;
    selectedKey = `collection:${id}`;
    void openEntity(id);
    renderMap();
    return;
  }

  if (type === 'more') {
    const [what, ref] = rest;
    closeMap();
    if (what === 'topics') {
      goScreen('collections');
    } else if (ref) {
      void openEntity(ref, mapSeasonId);
    } else {
      goScreen('series');
    }
  }
}

// ============ Превью при наведении ============

function buildHoverPreviewNode(video: AvgVideoVideo, previewUrl: string | null): HTMLVideoElement | null {
  if (video.access !== 'ok' || !previewUrl?.trim()) return null;

  const el = document.createElement('video');
  el.className = 'atv-thumb-hover-video';
  el.src = previewUrl;
  el.muted = true;
  el.loop = true;
  el.playsInline = true;
  el.preload = 'none';
  el.setAttribute('disablepictureinpicture', '');
  return el;
}

function stopHoverPreview(videoId?: string): void {
  if (videoId && activePreviewId && activePreviewId !== videoId) return;
  if (hoverPreviewTimer) {
    clearTimeout(hoverPreviewTimer);
    hoverPreviewTimer = null;
  }

  if (currentHoverVideo) {
    currentHoverVideo.pause();
  }
  currentHoverVideo = null;

  document.querySelectorAll<HTMLElement>('.atv-thumb.is-previewing').forEach((thumb) => {
    thumb.classList.remove('is-previewing');
    const slot = thumb.querySelector('.atv-thumb-hover');
    if (!slot) return;
    const videoEl = slot.querySelector<HTMLVideoElement>('video');
    if (videoEl) {
      videoEl.pause();
      videoEl.removeAttribute('src');
    }
    slot.innerHTML = '';
  });

  activePreviewId = null;
}

async function startHoverPreview(videoId: string): Promise<void> {
  if (!hoverPreviewEnabled) return;
  const video = getVideo(videoId);
  const thumb = document.querySelector<HTMLElement>(`.atv-thumb[data-atv-preview="${CSS.escape(videoId)}"]`);
  if (!video || !thumb) return;

  stopHoverPreview();

  const slot = thumb.querySelector<HTMLElement>('.atv-thumb-hover');
  if (!slot) return;

  let previewUrl = video.preview_url;
  if (needsServerPlayback(previewUrl)) {
    const resolved = await resolvePlayback(videoId);
    if (!resolved) return;
    previewUrl = resolved.preview_url;
  }

  const node = buildHoverPreviewNode(video, previewUrl);
  if (!node) return;

  slot.innerHTML = '';
  slot.appendChild(node);
  thumb.classList.add('is-previewing');
  activePreviewId = videoId;
  currentHoverVideo = node;

  void node.play().catch(() => stopHoverPreview(videoId));
}

function bindHoverPreview(): void {
  if (hoverPreviewBound || !hoverPreviewEnabled) return;
  hoverPreviewBound = true;

  const root = $('atv-video-root');
  if (!root) return;

  root.addEventListener('mouseover', (e) => {
    const thumb = (e.target as HTMLElement).closest<HTMLElement>('[data-atv-preview]');
    if (!thumb?.dataset.atvPreview) return;
    const id = thumb.dataset.atvPreview;
    if (activePreviewId === id) return;
    if (hoverPreviewTimer) clearTimeout(hoverPreviewTimer);
    hoverPreviewTimer = setTimeout(() => {
      void startHoverPreview(id);
    }, 400);
  });

  root.addEventListener('mouseout', (e) => {
    const thumb = (e.target as HTMLElement).closest<HTMLElement>('[data-atv-preview]');
    if (!thumb?.dataset.atvPreview) return;
    const related = e.relatedTarget;
    if (related instanceof Node && thumb.contains(related)) return;
    stopHoverPreview(thumb.dataset.atvPreview);
  });

  root.addEventListener('scroll', () => stopHoverPreview(), true);
  window.addEventListener('blur', () => stopHoverPreview());
}

// ============ События ============

function bindPlayerModal(): void {
  if (playerModalBound) return;
  playerModalBound = true;

  $('atv-player-close')?.addEventListener('click', closePlayer);
  $('atv-player-backdrop')?.addEventListener('click', closePlayer);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (!$('atv-map-modal')?.classList.contains('hidden')) {
      closeMap();
      return;
    }
    if (!$('atv-player-modal')?.classList.contains('hidden')) {
      closePlayer();
    }
  });

  // Кнопки навигации плеера живут внутри модалки — обработчик делегированный.
  $('atv-player-modal')?.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    const collapseBtn = target.closest<HTMLElement>('[data-atv-collapse-toggle]');
    if (collapseBtn) {
      toggleCollapsible(collapseBtn);
      return;
    }

    const mapBtn = target.closest<HTMLElement>('[data-atv-map-open]');
    if (mapBtn?.dataset.atvMapOpen) {
      openMapFromKey(mapBtn.dataset.atvMapOpen);
      return;
    }

    const playBtn = target.closest<HTMLElement>('[data-atv-play]');
    if (playBtn?.dataset.atvPlay) {
      void openPlayer(playBtn.dataset.atvPlay);
      return;
    }

    const entityBtn = target.closest<HTMLElement>('[data-atv-entity]');
    if (entityBtn?.dataset.atvEntity) {
      closePlayer();
      void openEntity(entityBtn.dataset.atvEntity);
      return;
    }

    const seasonBtn = target.closest<HTMLElement>('[data-atv-season-jump]');
    if (seasonBtn?.dataset.atvSeasonJump) {
      const [collectionId, seasonId] = seasonBtn.dataset.atvSeasonJump.split(':');
      closePlayer();
      if (collectionId) void openEntity(collectionId, seasonId || null);
    }
  });
}

function openMapFromKey(key: string): void {
  const [type, ...rest] = key.split(':');
  const id = rest.join(':');
  if (type === 'video') {
    mapTopicId = null;
    mapSeasonId = null;
    void openMap({ videoId: id, collectionId: null });
    return;
  }
  if (type === 'collection') {
    const collection = collections.find((c) => c.id === id) || collectionDetails.get(id)?.collection || null;
    if (collection?.kind === 'series') {
      mapMode = 'series';
      mapTopicId = null;
      void openMap({ videoId: null, collectionId: id });
      return;
    }

    // «По темам» строится вокруг видео: у подборки якорем берём первый материал.
    mapMode = 'topics';
    mapTopicId = id;
    const anchor = collectionDetails.get(id)?.items[0]?.video_id || collection?.video_ids[0] || null;
    if (anchor) {
      void openMap({ videoId: anchor, collectionId: id });
      return;
    }
    void loadCollectionDetail(id).then((detail) => {
      void openMap({ videoId: detail?.items[0]?.video_id || null, collectionId: id });
    });
  }
}

function bindMapModal(): void {
  if (mapBound) return;
  mapBound = true;

  $('atv-map-close')?.addEventListener('click', closeMap);
  $('atv-map-backdrop')?.addEventListener('click', closeMap);

  $('atv-map-modes')?.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-atv-map-mode]');
    if (!btn?.dataset.atvMapMode) return;
    mapMode = btn.dataset.atvMapMode === 'topics' ? 'topics' : 'series';
    mapDetailText = '';
    renderMap();
    void ensureMapData().then(() => {
      if (mapOpen) renderMap();
    });
  });

  const canvas = $('atv-map-canvas');
  canvas?.addEventListener('click', (e) => {
    const node = (e.target as HTMLElement).closest<SVGGElement>('[data-atv-map-node]');
    const key = node?.getAttribute('data-atv-map-node');
    if (!key) return;
    handleMapNode(key, node?.getAttribute('aria-label') || '');
  });

  // Узлы карты — фокусируемые группы SVG: клавиатура работает наравне с мышью.
  canvas?.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    const node = (e.target as HTMLElement).closest<SVGGElement>('[data-atv-map-node]');
    const key = node?.getAttribute('data-atv-map-node');
    if (!key) return;
    e.preventDefault();
    handleMapNode(key, node?.getAttribute('aria-label') || '');
  });
}

function bindUserEvents(): void {
  if (userEventsBound) return;
  userEventsBound = true;

  // Смена ширины меняет и число строк: текст, помещавшийся в три строки на
  // широком экране, на узком обрезается — кнопку нужно пересчитать.
  window.addEventListener('resize', () => {
    syncCollapsibles($('atv-video-root'));
    syncCollapsibles($('atv-player-meta'));
  });

  $('atv-tabs')?.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-atv-screen]');
    if (!btn?.dataset.atvScreen) return;
    goScreen(btn.dataset.atvScreen as AtvScreen);
  });

  $('atv-crumbs')?.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-atv-screen]');
    if (!btn?.dataset.atvScreen) return;
    goScreen(btn.dataset.atvScreen as AtvScreen);
  });

  $('atv-categories')?.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-atv-rubric]');
    if (!btn) return;
    const rubricId = btn.dataset.atvRubric || 'all';
    activeRubricId = rubricId === 'all' ? null : rubricId;
    activeSubrubricId = null;
    renderAll();
    reloadGrid();
  });

  $('atv-subrubrics')?.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-atv-subrubric]');
    if (!btn) return;
    activeSubrubricId = btn.dataset.atvSubrubric || null;
    renderAll();
    reloadGrid();
  });

  $('atv-search')?.addEventListener('input', (e) => {
    activeSearch = (e.target as HTMLInputElement).value;
    // На обзоре искать не по чему: запрос уводит в общий список каталога,
    // внутри подборки или сериала — ищет только по их составу.
    if (screen === 'home' && activeSearch.trim()) screen = 'all';

    // Состав открытой сущности фильтруется на месте, в renderAll(); на сервер
    // уходит только общий список — и не на каждый символ, а после паузы.
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    if (screen === 'all') {
      // Ответа ещё нет, но показанный список уже не отвечает запросу — экран
      // честно говорит «загрузка», а не «ничего не найдено».
      gridLoading = true;
      searchDebounceTimer = setTimeout(() => {
        searchDebounceTimer = null;
        reloadGrid();
      }, SEARCH_DEBOUNCE_MS);
    }
    renderAll();
  });

  $('atv-sort')?.addEventListener('change', (e) => {
    activeSort = (e.target as HTMLSelectElement).value;
    renderAll();
    if (screen === 'all') reloadGrid();
  });

  $('atv-load-more')?.addEventListener('click', () => {
    if (gridLoading || gridItems.length >= gridTotal) return;
    void loadGridPage({ append: true });
  });

  $('atv-map-btn')?.addEventListener('click', () => {
    const target = resolveMapTarget();
    if (target.videoId) {
      openMapFromKey(`video:${target.videoId}`);
      return;
    }
    if (target.collectionId) {
      openMapFromKey(`collection:${target.collectionId}`);
      return;
    }
    showToast('Выберите видео или подборку — карта строится вокруг них');
  });

  $('atv-cta-routes')?.addEventListener('click', () => {
    void switchView('examples');
  });

  $('atv-video-root')?.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-atv-anno]')) return;

    // Раньше кнопок раскрытия: она живёт внутри карточек и шапок, клик по ним
    // иначе увёл бы на экран сущности.
    const collapseBtn = target.closest<HTMLElement>('[data-atv-collapse-toggle]');
    if (collapseBtn) {
      toggleCollapsible(collapseBtn);
      return;
    }

    const mapBtn = target.closest<HTMLElement>('[data-atv-map-open]');
    if (mapBtn?.dataset.atvMapOpen) {
      openMapFromKey(mapBtn.dataset.atvMapOpen);
      return;
    }

    const entityBtn = target.closest<HTMLElement>('[data-atv-entity]');
    if (entityBtn?.dataset.atvEntity) {
      void openEntity(entityBtn.dataset.atvEntity);
      return;
    }

    const seasonBtn = target.closest<HTMLElement>('[data-atv-season]');
    if (seasonBtn?.dataset.atvSeason) {
      void selectSeason(seasonBtn.dataset.atvSeason);
      return;
    }

    const playBtn = target.closest<HTMLElement>('[data-atv-play]');
    if (playBtn?.dataset.atvPlay) {
      void openPlayer(playBtn.dataset.atvPlay);
    }
  });
}

export async function loadAvgVideoView(): Promise<void> {
  bindPlayerModal();
  bindMapModal();
  bindHoverPreview();
  bindUserEvents();

  const grid = $('atv-video-grid');
  if (grid) grid.innerHTML = renderEmpty('Загрузка видео…');
  const entries = $('atv-entries-host');
  if (entries) entries.innerHTML = renderEmpty('Загрузка каталога…');

  try {
    await Promise.all([loadRubrics(), loadContinueAndCollections()]);

    const r = await fetch(`/api/avg-video/videos?limit=${HOME_INDEX_LIMIT}&sort=newest`, {
      headers: authHeaders(),
    });
    if (!r.ok) throw new Error('Не удалось загрузить видео');
    const data = await r.json() as { items?: AvgVideoVideo[] };
    homeItems = data.items || [];
    indexVideos(homeItems);
    renderAll();
    if (screen === 'all') reloadGrid();
  } catch (err) {
    if (grid) grid.innerHTML = renderEmpty('Не удалось загрузить видео.');
    if (entries) entries.innerHTML = renderEmpty('Не удалось загрузить каталог.');
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  }
}

export function initAvgVideo(): void {
  bindPlayerModal();
  bindMapModal();
  bindHoverPreview();
  bindUserEvents();
}
